import { DiscoverResult, Terminal } from "@stripe/terminal-js";
import { CircleCrossImage, CirclePlusImage, ComputerImage, EmvImage } from "@xola/icons";
import { Alert, flash, Modal, SubmitButton } from "@xola/ui-kit";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { EmvDevice } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useStripeTerminal } from "../../../hooks/useStripeTerminal";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface PairModalProps {
    device: EmvDevice;
    isOpen: boolean;
    onCancel: () => void;
    onSubmit: () => void;
}

export const PairDeviceModal = ({ device, isOpen, onCancel, onSubmit }: PairModalProps) => {
    // This is in the main modal so that it's done as soon as the hardware page loads
    // Then when pairing needs to be done, it's instant instead of waiting for loading the terminal

    return (
        <Modal isOpen={isOpen} onClose={onCancel}>
            <ModalContent device={device} onCancel={onCancel} onSubmit={onSubmit} />
        </Modal>
    );
};

interface ModalContentProps {
    device: EmvDevice;
    onCancel: () => void;
    onSubmit: () => void;
}

const ModalContent = ({ device, onCancel, onSubmit }: ModalContentProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [isLoading, setIsLoading] = useState(false);
    const computer = JSON.parse(localStorage.getItem("computer") ?? "{}");
    const isPaired = device?.computer?.id && device?.computer?.id === computer.id;

    const readerTerminal = useStripeTerminal();

    const { mutateAsync: saveComputer } = useMutation(async (values: EmvDevice) => {
        return api.post(`/users/${seller.id}/preferences/stripeTerminal/paymentDevices/${device.id}/computer`, values);
    });

    const { mutateAsync: deleteComputer } = useMutation(async () => {
        return api.delete(
            `/users/${seller.id}/preferences/stripeTerminal/paymentDevices/${device.id}/computer/${computer.id}`,
        );
    });

    const unPairDevice = async (readerTerminal: Terminal) => {
        try {
            disconnectReader(readerTerminal);
        } catch (error) {
            console.log("EMV: error disconnecting reader", error);
        }

        if (!computer.id) {
            flash.danger("Error un-pairing device. POS Station label does not exist");
            throw new Error("Error un-pairing device. No POS Station label");
        }

        if (!device.id) {
            flash.danger("Error un-pairing device. No device ID");
            throw new Error("Error un-pairing device. No device ID");
        }

        await deleteComputer();
    };

    const disconnectReader = (readerTerminal: Terminal) => {
        if (readerTerminal) {
            readerTerminal.disconnectReader();
            readerTerminal.clearCachedCredentials();
        }

        invalidateQueries();
    };

    const invalidateQueries = () => {
        invalidate("sellers", seller.id);
        invalidate("users", seller.id);
        invalidate("paymentDevices");
    };

    const pairDevice = async (readerTerminal: Terminal) => {
        if (!computer.id) {
            flash.danger("Cannot pair device. POS Station Label is not set");
            throw new Error("Error un-pairing device. No computer ID");
        }

        const location = seller.preferences.stripeTerminal.location.remoteId;
        const readerId = device.remoteId;

        const config = { simulated: false, location };
        const discoverResult = (await readerTerminal.discoverReaders(config)) as DiscoverResult;
        const selectedReader = discoverResult.discoveredReaders.find((reader) => reader.id === readerId);
        if (!selectedReader) {
            flash.show({
                text: "Could not find your reader. Please remove it and try adding it again",
                color: "danger",
            });
            onCancel();
            setIsLoading(false);
            return;
        }

        /** If any other connection already exists, disconnect that */
        await readerTerminal.disconnectReader();

        const connectResult = await readerTerminal.connectReader(selectedReader);

        setIsLoading(false);

        if ("reader" in connectResult && connectResult?.reader) {
            try {
                await saveComputer(computer);
            } catch (error) {
                console.log("EMV: error saving computer", error);
            }

            console.log("EMV Hardware: Connected to reader:", connectResult.reader.label);
            flash.success("Device successfully paired");
            onSubmit();
            return;
        }

        if ("error" in connectResult && connectResult?.error) {
            console.log("EMV Hardware: Failed to connect:", connectResult.error);
            flash.show({ text: connectResult.error?.message ?? "Failed to connect to the reader", color: "danger" });
        } else {
            console.log("EMV Hardware: Invalid response", connectResult);
            flash.danger("Failed to get a response from the reader");
        }
    };

    const handleSubmit = async () => {
        if (!readerTerminal) {
            console.warn("EMV: Stripe terminal not initialized. Cannot pair");
            return;
        }

        setIsLoading(true);

        try {
            await (isPaired ? unPairDevice(readerTerminal) : pairDevice(readerTerminal));
        } catch {
            setIsLoading(false);
            return;
        }

        invalidateQueries();
        onSubmit();
        setIsLoading(false);
    };

    const message = isPaired ? (
        <>
            <var data-var="deviceLabel">{device.label}</var> will be unpaired from&nbsp;
            <var data-var="computerLabel">{computer.label}</var>
        </>
    ) : (
        <>
            <var data-var="deviceLabel">{device.label}</var> will be paired with&nbsp;
            <var data-var="computerLabel">{computer.label}</var>
        </>
    );

    return (
        <>
            <Modal.Header description={computer.label && message}>{isPaired ? "Unpair" : "Pair"} Device</Modal.Header>
            <Modal.Body>
                <div className="flex flex-row items-center justify-center">
                    {computer.label ? (
                        <>
                            <EmvImage />
                            {isPaired ? <CircleCrossImage /> : <CirclePlusImage />}
                            <ComputerImage />
                        </>
                    ) : (
                        <Alert color="danger">This device cannot be paired because no POS Station Label is set</Alert>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="space-x-4">
                <SecondaryButton disabled={isLoading} onClick={onCancel}>
                    Cancel
                </SecondaryButton>

                {isPaired ? (
                    <SubmitButton isLoading={isLoading} type="button" color="danger" onClick={handleSubmit}>
                        Unpair
                    </SubmitButton>
                ) : (
                    <SubmitButton isLoading={isLoading} type="button" color="primary" onClick={handleSubmit}>
                        Pair
                    </SubmitButton>
                )}
            </Modal.Footer>
        </>
    );
};
